import { useEffect, useRef, useState } from "react";
import { api, formatBRL } from "../../lib/api";
import { Plus, Edit2, Trash2, Users, Upload, Star, ArrowUp, ArrowDown, X, Video, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", type: "sala_reuniao", category: "", capacity: 1, description: "",
  resources: [], photos: [], video_url: "", hourly_rate: 0, half_day_rate: 0, daily_rate: 0, active: true,
};

const TYPES = [
  ["escritorio_executivo", "Escritório Executivo"],
  ["consultorio", "Consultório"],
  ["sala_reuniao", "Sala de Reunião"],
  ["sala_privativa", "Sala Privativa"],
  ["estacao", "Estação Coworking"],
  ["mesa", "Mesa"],
  ["auditorio", "Auditório"],
];

// Resolve image URL: API-prefixed paths must use REACT_APP_BACKEND_URL
function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

export default function Espacos() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [occupancy, setOccupancy] = useState({}); // { space_id: {occupancy_pct, hours_used, hours_available} }
  const fileRef = useRef(null);

  const load = () => api.get("/spaces").then(({ data }) => setItems(data));
  const loadOccupancy = () =>
    api.get("/reports/ops-kpis").then(({ data }) => {
      const map = {};
      for (const o of (data?.occupancy || [])) map[o.space_id] = o;
      setOccupancy(map);
    }).catch(() => {});
  useEffect(() => { load(); loadOccupancy(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      capacity: +form.capacity,
      hourly_rate: +form.hourly_rate,
      half_day_rate: +form.half_day_rate,
      daily_rate: +form.daily_rate,
      video_url: form.video_url || null,
      resources: typeof form.resources === "string"
        ? form.resources.split(",").map((s) => s.trim()).filter(Boolean)
        : form.resources,
    };
    try {
      if (editing) await api.put(`/spaces/${editing}`, payload);
      else await api.post("/spaces", payload);
      toast.success("Espaço salvo");
      setForm(empty); setEditing(null);
      load();
    } catch { toast.error("Erro ao salvar"); }
  };

  const edit = (s) => {
    setForm({
      ...empty,
      ...s,
      resources: Array.isArray(s.resources) ? s.resources.join(", ") : (s.resources || ""),
      video_url: s.video_url || "",
      photos: s.photos || [],
    });
    setEditing(s.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!confirm("Remover este espaço?")) return;
    await api.delete(`/spaces/${id}`);
    toast.success("Removido"); load();
  };

  const cancel = () => { setEditing(null); setForm(empty); };

  // ---------- Galeria de fotos ----------
  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const next = [...(form.photos || [])];
    try {
      for (const f of Array.from(files)) {
        if (f.size > 8 * 1024 * 1024) {
          toast.error(`${f.name}: maior que 8MB, ignorada`);
          continue;
        }
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(f);
        });
        const { data } = await api.post("/uploads/image", { filename: f.name, content_base64: b64 });
        next.push(data.url);
      }
      setForm((s) => ({ ...s, photos: next }));
      toast.success(`${files.length} foto(s) enviada(s)`);
    } catch (e) {
      toast.error("Erro no upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (idx) => {
    setForm((s) => ({ ...s, photos: s.photos.filter((_, i) => i !== idx) }));
  };
  const movePhoto = (idx, dir) => {
    setForm((s) => {
      const arr = [...s.photos];
      const ni = idx + dir;
      if (ni < 0 || ni >= arr.length) return s;
      [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
      return { ...s, photos: arr };
    });
  };
  const setAsMain = (idx) => {
    setForm((s) => {
      const arr = [...s.photos];
      const [main] = arr.splice(idx, 1);
      arr.unshift(main);
      return { ...s, photos: arr };
    });
  };

  return (
    <div data-testid="admin-espacos">
      <div className="admin-page-header">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mb-2">Espaços</div>
          <h1 className="font-display text-2xl sm:text-3xl text-[#14213D] font-light">Gestão de Espaços</h1>
        </div>
      </div>

      <form onSubmit={save} className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-6 space-y-4" data-testid="space-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input required placeholder="Nome *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm">
            {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input placeholder="Categoria (ex: Coworking)" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />
          <input type="number" min="1" placeholder="Capacidade" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" />
          <input type="number" step="0.01" placeholder="Valor/hora (R$)" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" data-testid="space-hourly-rate" />
          <input type="number" step="0.01" placeholder="Meia diária — 4h (R$)" value={form.half_day_rate} onChange={(e) => setForm({ ...form, half_day_rate: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" data-testid="space-half-day-rate" />
          <input type="number" step="0.01" placeholder="Valor/dia (R$)" value={form.daily_rate} onChange={(e) => setForm({ ...form, daily_rate: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm" data-testid="space-daily-rate" />
          <input placeholder="Recursos (separados por vírgula)" value={form.resources} onChange={(e) => setForm({ ...form, resources: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm md:col-span-3" />
          <textarea placeholder="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border border-gray-200 rounded px-3 py-2 text-sm md:col-span-3" rows={2} />
        </div>

        {/* Vídeo */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2 flex items-center gap-1"><Video size={12} /> Vídeo do ambiente (opcional)</div>
          <input
            placeholder="Cole a URL do YouTube ou link MP4 direto"
            value={form.video_url}
            onChange={(e) => setForm({ ...form, video_url: e.target.value })}
            className="w-full border border-gray-200 rounded px-3 py-2 text-sm"
            data-testid="space-video-url"
          />
          <div className="text-[11px] text-gray-400 mt-1">Aceita: YouTube (https://youtube.com/watch?v=...) ou arquivo MP4 público.</div>
        </div>

        {/* Galeria de fotos */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2 flex items-center gap-1"><ImageIcon size={12} /> Galeria de fotos</div>
          <div className="flex flex-wrap gap-3" data-testid="space-photos-gallery">
            {(form.photos || []).map((p, idx) => (
              <div key={`${p}-${idx}`} className={`relative group rounded-md overflow-hidden border-2 ${idx === 0 ? "border-[#D4AF37]" : "border-gray-200"}`} style={{ width: 140 }}>
                <img src={resolveUrl(p)} alt="" className="w-full h-24 object-cover" />
                {idx === 0 && (
                  <div className="absolute top-1 left-1 bg-[#D4AF37] text-[#14213D] text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded">Principal</div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-black/55 backdrop-blur-sm p-1 flex justify-between gap-1 opacity-0 group-hover:opacity-100 transition">
                  <button type="button" onClick={() => movePhoto(idx, -1)} disabled={idx === 0} className="text-white disabled:opacity-30 p-1" title="Mover esquerda" data-testid={`photo-left-${idx}`}><ArrowUp size={12} className="-rotate-90" /></button>
                  <button type="button" onClick={() => setAsMain(idx)} disabled={idx === 0} className="text-white disabled:opacity-30 p-1" title="Definir como principal" data-testid={`photo-main-${idx}`}><Star size={12} /></button>
                  <button type="button" onClick={() => movePhoto(idx, 1)} disabled={idx === (form.photos.length - 1)} className="text-white disabled:opacity-30 p-1" title="Mover direita" data-testid={`photo-right-${idx}`}><ArrowDown size={12} className="-rotate-90" /></button>
                  <button type="button" onClick={() => removePhoto(idx)} className="text-red-300 hover:text-red-100 p-1" title="Remover" data-testid={`photo-del-${idx}`}><X size={12} /></button>
                </div>
              </div>
            ))}
            <label className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-300 hover:border-[#D4AF37] rounded-md cursor-pointer text-gray-500 hover:text-[#7a6519] transition ${uploading ? "opacity-50 pointer-events-none" : ""}`} style={{ width: 140, height: 96 }} data-testid="upload-photos-btn">
              <Upload size={20} />
              <span className="text-[11px]">{uploading ? "Enviando…" : "Adicionar"}</span>
              <input ref={fileRef} type="file" accept="image/*" multiple onChange={(e) => handleFiles(e.target.files)} className="hidden" />
            </label>
          </div>
          <div className="text-[11px] text-gray-400 mt-2">A primeira foto é a capa. Arraste para reordenar, use ⭐ para definir a principal.</div>
        </div>

        <div className="flex gap-3 pt-2">
          <button type="submit" className="bg-[#14213D] text-white font-semibold px-5 py-2.5 rounded text-sm inline-flex items-center gap-2" data-testid="save-space">
            <Plus size={14} /> {editing ? "Atualizar" : "Adicionar"} espaço
          </button>
          {editing && <button type="button" onClick={cancel} className="border border-gray-300 px-5 py-2.5 rounded text-sm">Cancelar</button>}
          <label className="ml-auto flex items-center gap-2 text-sm self-center">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Ativo
          </label>
        </div>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <div key={s.id} className={`bg-white border rounded-lg overflow-hidden hover:shadow-md transition ${s.active === false ? "opacity-60" : ""}`} data-testid={`space-${s.id}`}>
            {s.photos?.[0] ? (
              <div className="aspect-video bg-gray-100 overflow-hidden relative">
                <img src={resolveUrl(s.photos[0])} alt={s.name} className="w-full h-full object-cover" />
                {s.photos.length > 1 && (
                  <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1"><ImageIcon size={10} /> {s.photos.length}</div>
                )}
                {s.video_url && (
                  <div className="absolute top-2 right-2 bg-[#D4AF37] text-[#14213D] text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1"><Video size={10} /> Vídeo</div>
                )}
              </div>
            ) : (
              <div className="aspect-video bg-gradient-to-br from-[#14213D] to-[#1F335E] flex items-center justify-center text-[#D4AF37] font-display text-3xl">{s.name?.[0]}</div>
            )}
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{s.category || s.type}</div>
              <div className="font-semibold text-[#14213D] mb-1">{s.name}</div>
              <div className="text-xs text-gray-500 mb-2 line-clamp-2">{s.description}</div>
              <div className="flex items-center gap-3 text-xs text-gray-600 mb-3">
                <span className="flex items-center gap-1"><Users size={12} />{s.capacity}</span>
                {s.hourly_rate > 0 && <span>{formatBRL(s.hourly_rate)}/h</span>}
                {s.half_day_rate > 0 && <span>{formatBRL(s.half_day_rate)}/4h</span>}
                {s.daily_rate > 0 && <span>{formatBRL(s.daily_rate)}/dia</span>}
              </div>
              {/* Ocupação (mês corrente) — reutiliza dados de reservas confirmadas */}
              {occupancy[s.id] && (
                <div className="mb-3" data-testid={`space-occupancy-${s.id}`}>
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                    <span>Ocupação (mês)</span>
                    <span>
                      {occupancy[s.id].occupancy_pct}% ·
                      {" "}{occupancy[s.id].hours_used}h de {occupancy[s.id].hours_available}h
                    </span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#14213D] to-[#D4AF37]"
                      style={{ width: `${Math.min(100, occupancy[s.id].occupancy_pct)}%` }}
                    />
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => edit(s)} className="flex-1 border border-gray-200 rounded py-1.5 text-xs flex items-center justify-center gap-1 hover:bg-gray-50" data-testid={`edit-space-${s.id}`}><Edit2 size={12} /> Editar</button>
                <button onClick={() => remove(s.id)} className="border border-red-200 text-red-600 rounded px-2 py-1.5 text-xs hover:bg-red-50" data-testid={`del-space-${s.id}`}><Trash2 size={12} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
